import React, { useEffect, useRef, useCallback } from 'react';
import { DEAL_ENDED, displayStatus, getStatusFilterCounts, sortOffers } from '../utils';
import './OffersToolbar.css';

/**
 * OffersToolbar — search + status chip row + sort + refresh.
 * Persists state in localStorage under cutthe6.offers.toolbar.
 */

const STORAGE_KEY = 'cutthe6.offers.toolbar';

const SORT_OPTIONS = [
  { value: 'newest',    label: 'Newest' },
  { value: 'oldest',    label: 'Oldest' },
  { value: 'priceDesc', label: 'Price (high→low)' },
  { value: 'priceAsc',  label: 'Price (low→high)' },
  { value: 'status',    label: 'By status' },
];

const STATUS_CHIPS = [
  { value: 'all',       label: 'All' },
  { value: 'pending',   label: 'Pending' },
  { value: 'countered', label: 'Countered' },
  { value: 'accepted',  label: 'Accepted' },
  { value: 'other',     label: 'Other' },
];

export const DEFAULT_TOOLBAR_STATE = {
  search: '',
  statusFilter: 'all',
  sort: 'newest',
};

/** Pure helper — apply a toolbar state to an offers array. */
export function applyToolbarState(offers = [], state = DEFAULT_TOOLBAR_STATE) {
  const { search = '', statusFilter = 'all', sort = 'newest' } = state || {};
  let result = offers;

  if (statusFilter && statusFilter !== 'all') {
    if (statusFilter === 'other') {
      result = result.filter((o) =>
        o.status === 'rejected' || o.status === 'withdrawn' || o.status === 'expired' || o.status === 'closed'
        || displayStatus(o) === DEAL_ENDED
      );
    } else {
      result = result.filter((o) => displayStatus(o) === statusFilter);
    }
  }

  const term = String(search).trim().toLowerCase();
  if (term) {
    result = result.filter((o) =>
      String(o.property_address || '').toLowerCase().includes(term)
    );
  }

  return sortOffers(result, sort);
}

export default function OffersToolbar({
  offers = [],
  state = DEFAULT_TOOLBAR_STATE,
  onChange,
  onRefresh,
  loading = false,
}) {
  const hydratedRef = useRef(false);
  const counts = getStatusFilterCounts(offers);

  // Hydrate from localStorage exactly once.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const next = {
          search: typeof parsed.search === 'string' ? parsed.search : DEFAULT_TOOLBAR_STATE.search,
          statusFilter: STATUS_CHIPS.some((c) => c.value === parsed.statusFilter)
            ? parsed.statusFilter
            : DEFAULT_TOOLBAR_STATE.statusFilter,
          sort: SORT_OPTIONS.some((s) => s.value === parsed.sort)
            ? parsed.sort
            : DEFAULT_TOOLBAR_STATE.sort,
        };
        if (typeof onChange === 'function') onChange(next);
      }
    } catch (_e) {
      // ignore corrupt storage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback((merged) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (_e) {
      // storage full / disabled — ignore
    }
  }, []);

  const update = useCallback(
    (patch) => {
      const merged = { ...state, ...patch };
      persist(merged);
      if (typeof onChange === 'function') onChange(patch);
    },
    [state, persist, onChange]
  );

  return (
    <div className="offers-toolbar">
      <div className="offers-toolbar__top">
        <div className="offers-toolbar__search-wrap">
          <label htmlFor="offers-toolbar-search" className="offers-sr-only">
            Search by address
          </label>
          <input
            id="offers-toolbar-search"
            type="search"
            className="offers-toolbar__search"
            placeholder="Search by address…"
            value={state.search || ''}
            onChange={(e) => update({ search: e.target.value })}
          />
          {state.search ? (
            <button
              type="button"
              className="offers-toolbar__search-clear"
              aria-label="Clear search"
              onClick={() => update({ search: '' })}
            >
              ×
            </button>
          ) : null}
        </div>

        <div className="offers-toolbar__sort-wrap">
          <label htmlFor="offers-toolbar-sort" className="offers-toolbar__sort-label">
            Sort:
          </label>
          <select
            id="offers-toolbar-sort"
            className="offers-toolbar__sort"
            value={state.sort || 'newest'}
            onChange={(e) => update({ sort: e.target.value })}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="offers-toolbar__refresh"
          aria-label="Refresh offers"
          disabled={loading}
          onClick={() => {
            if (typeof onRefresh === 'function') onRefresh();
          }}
        >
          <span aria-hidden="true">↻</span>
        </button>
      </div>

      <div
        className="offers-toolbar__chips"
        role="group"
        aria-label="Filter by status"
      >
        {STATUS_CHIPS.map((chip) => {
          const active = (state.statusFilter || 'all') === chip.value;
          const count = counts[chip.value] ?? 0;
          return (
            <button
              key={chip.value}
              type="button"
              className={
                'offers-toolbar__chip' +
                (active ? ' offers-toolbar__chip--active' : '')
              }
              aria-pressed={active}
              onClick={() => update({ statusFilter: chip.value })}
            >
              <span className="offers-toolbar__chip-label">{chip.label}</span>
              <span className="offers-toolbar__chip-count" aria-hidden="true">
                {count}
              </span>
              <span className="offers-sr-only">({count} offers)</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
